/**
 * Copyright (C) 2020 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 *
 */
import styled from 'styled-components';

/**
 * Wraps the chart container with the given CSS. Note that the highcharts theme is overwritten here.
 * Highcharts default theme used in this application is the Dark Unica theme:
 *  https://www.highcharts.com/docs/chart-design-and-style/custom-themes-in-styled-mode
 */
const ChartsSeriesColorWrapper = styled.div`
  /* Highcharts series color classnames */

  .highcharts-axis-line,
  .highcharts-yaxis-grid .highcharts-grid-line,
  .highcharts-xaxis-grid .highcharts-grid-line {
    stroke-width: 1px;
    stroke: #1c262f;
  }

  .highcharts-axis-title,
  .highcharts-axis-labels {
    fill: #9fafbc;
  }

  .highcharts-legend-item {
    color: #9fafbc;
  }

  .highcharts-legend-item.highcharts-column-series > rect {
    width: 6px;
    height: 9.7px;
    rx: 0;
    ry: 0;
    x: 5;
    y: 6;
  }

  .ftv__charts__series--canaryYellow .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--canaryYellow,
  .ftv__charts__series--canaryYellow {
    fill: #ffdb7d;
    stroke: #ffdb7d;
    stroke-width: 2;
  }

  .ftv__charts__series--burntOrange .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--burntOrange,
  .ftv__charts__series--burntOrange {
    fill: #ef8201;
    stroke: #ef8201;
    stroke-width: 2;
  }

  .ftv__charts__series--purple .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--purple,
  .ftv__charts__series--purple {
    fill: #724ae8;
    stroke: #724ae8;
    stroke-width: 2;
  }

  .ftv__charts__series--turquoise .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--turquoise,
  .ftv__charts__series--turquoise {
    fill: #00c2de;
    stroke: #00c2de;
    stroke-width: 2;
  }

  .ftv__charts__series--lightPurple--dashed .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--lightPurple--dashed,
  .ftv__charts__series--lightPurple--dashed {
    fill: #baa8f0;
    stroke: #baa8f0;
    stroke-dasharray: 3px;
    stroke-linecap: butt;
    stroke-width: 1;
    .highcharts-point {
      display: none;
    }
    .highcharts-graph {
      stroke-dasharray: 3px;
      stroke-linecap: butt;
      stroke-width: 1;
    }
  }

  .ftv__charts__series--lightTurqoise--dashed .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--lightTurqoise--dashed,
  .ftv__charts__series--lightTurqoise--dashed {
    fill: #8df2ed;
    stroke: #8df2ed;
    stroke-dasharray: 3px;
    stroke-linecap: butt;
    stroke-width: 1;
    .highcharts-point {
      display: none;
    }
    .highcharts-graph {
      stroke-dasharray: 3px;
      stroke-linecap: butt;
      stroke-width: 1;
    }
  }

  .ftv__charts__series--magenta .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--magenta,
  .ftv__charts__series--magenta {
    fill: #d147af;
    stroke: #d147af;
    stroke-width: 2;
  }

  .ftv__charts__series--skyBlue .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--skyBlue,
  .ftv__charts__series--skyBlue {
    fill: #14b9ff;
    stroke: #14b9ff;
    stroke-width: 2;
  }

  .ftv__charts__series--lightPink--dashed .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--lightPink--dashed,
  .ftv__charts__series--lightPink--dashed {
    fill: #f9aae4;
    stroke: #f9aae4;
    stroke-dasharray: 3px;
    stroke-linecap: butt;
    stroke-width: 1;
    .highcharts-point {
      display: none;
    }
    .highcharts-graph {
      stroke-dasharray: 3px;
      stroke-linecap: butt;
      stroke-width: 1;
    }
  }

  .ftv__charts__series--lightSkyBlue--dashed .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--lightSkyBlue--dashed,
  .ftv__charts__series--lightSkyBlue--dashed {
    fill: #9be1ff;
    stroke: #9be1ff;
    stroke-dasharray: 3px;
    stroke-linecap: butt;
    stroke-width: 1;
    .highcharts-point {
      display: none;
    }
    .highcharts-graph {
      stroke-dasharray: 3px;
      stroke-linecap: butt;
      stroke-width: 1;
    }
  }

  .ftv__charts__series--freshCutGrassGreen .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--freshCutGrassGreen,
  .ftv__charts__series--freshCutGrassGreen {
    fill: #a4d810;
    stroke: #a4d810;
    stroke-width: 2;
  }

  .ftv__charts__series--standardOrange .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--standardOrange,
  .ftv__charts__series--standardOrange {
    fill: #ffb029;
    stroke: #ffb029;
    stroke-width: 2;
  }

  .ftv__charts__series--creamyWhite .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--creamyWhite,
  .ftv__charts__series--creamyWhite {
    fill: #ffe9b3;
    stroke: #ffe9b3;
    stroke-width: 2;
  }

  .ftv__charts__series--vibrantPink .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--vibrantPink,
  .ftv__charts__series--vibrantPink {
    fill: #ea7bce;
    stroke: #ea7bce;
    stroke-width: 2;
  }

  .ftv__charts__series--cozyTurquoise .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--cozyTurquoise,
  .ftv__charts__series--cozyTurquoise {
    fill: #18ecfc;
    stroke: #18ecfc;
    stroke-width: 2;
  }

  .ftv__charts__series--violetDelight .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--violetDelight,
  .ftv__charts__series--violetDelight {
    fill: #baa8f0;
    stroke: #baa8f0;
    stroke-width: 2;
  }

  .ftv__charts__series--slimeGreen .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--slimeGreen,
  .ftv__charts__series--slimeGreen {
    fill: #daee6b;
    stroke: #daee6b;
    stroke-width: 2;
  }

  .ftv__charts__series--emoPink .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--emoPink,
  .ftv__charts__series--emoPink {
    fill: #ad258b;
    stroke: #ad258b;
    stroke-width: 2;
  }

  .ftv__charts__series--darkForestGreen .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--darkForestGreen,
  .ftv__charts__series--darkForestGreen {
    fill: #358000;
    stroke: #358000;
    stroke-width: 2;
  }

  .ftv__charts__series--tryMeTeal .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--tryMeTeal,
  .ftv__charts__series--tryMeTeal {
    fill: #00a2c0;
    stroke: #00a2c0;
    stroke-width: 2;
  }

  .ftv__charts__series--earlGrey .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--earlGrey,
  .ftv__charts__series--earlGrey {
    fill: #808080;
    stroke: #808080;
    stroke-width: 2;
  }

  .ftv__charts__series--vividRedOrange .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--vividRedOrange,
  .ftv__charts__series--vividRedOrange {
    fill: #f6554c;
    stroke: #f6554c;
    stroke-width: 2;
  }

  .ftv__charts__series--beige--dashed .highcharts-point,
  .highcharts-tooltip .ftv__charts__series--beige--dashed,
  .ftv__charts__series--beige--dashed {
    fill: #c7bb95;
    stroke: #c7bb95;
    stroke-dasharray: 3px;
    stroke-linecap: butt;
    stroke-width: 1;
    .highcharts-point {
      display: none;
    }
    .highcharts-graph {
      stroke-dasharray: 3px;
      stroke-linecap: butt;
      stroke-width: 1;
    }
  }
`;

export default ChartsSeriesColorWrapper;
